'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Carousel from '@/components/carousel/Carousel';
import { cn } from '@/lib/cn';
import { IconChevronLeft, IconChevronRight, IconCross } from '@repo/svgs';
import NextImage from 'next/image';

export type FullScreenCarouselItem = {
  id: string;
  src: string;
  alt: string;
};

export interface FullScreenCarouselProps {
  open: boolean;
  carouselId: string;
  items: FullScreenCarouselItem[];
  initialIndex?: number;
  title?: string;
  onClose: () => void;
}

export function FullScreenCarousel({
  open,
  carouselId,
  items,
  initialIndex = 0,
  title,
  onClose,
}: FullScreenCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (items.length <= 1) return;

      if (e.key === 'ArrowLeft') {
        document.dispatchEvent(
          new CustomEvent('carousel-control', {
            detail: { carouselId, action: 'prev' },
          }),
        );
      }
      if (e.key === 'ArrowRight') {
        document.dispatchEvent(
          new CustomEvent('carousel-control', {
            detail: { carouselId, action: 'next' },
          }),
        );
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [carouselId, items.length, onClose, open]);

  useEffect(() => {
    if (!open) return;
    setCurrentIndex(initialIndex);

    const goTo = () => {
      document.dispatchEvent(
        new CustomEvent('carousel-control', {
          detail: { carouselId, action: 'goto', index: initialIndex },
        }),
      );
    };

    // Ensure CarouselClient has mounted before dispatching.
    requestAnimationFrame(() => requestAnimationFrame(goTo));
  }, [carouselId, initialIndex, open]);

  useEffect(() => {
    if (!open) return;
    const handleCarouselUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{ carouselId: string; currentIndex: number }>;
      if (customEvent.detail?.carouselId !== carouselId) return;
      setCurrentIndex(customEvent.detail.currentIndex);
    };
    document.addEventListener('carousel-update', handleCarouselUpdate);
    return () => document.removeEventListener('carousel-update', handleCarouselUpdate);
  }, [carouselId, open]);

  if (!open) return null;

  const dispatchControl = (action: 'prev' | 'next') => {
    document.dispatchEvent(
      new CustomEvent('carousel-control', {
        detail: { carouselId, action },
      }),
    );
  };

  const fullscreen = (
    <div
      className="fixed inset-0 z-60 bg-black flex flex-col pb-4"
      role="dialog"
      aria-modal="true"
      aria-label="Fullscreen carousel"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Mobile */}
      <div className="md:hidden flex items-center justify-between px-4 py-4">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={cn('p-3 rounded-full')}
        >
          <IconChevronLeft className="h-4 w-4 icon-inverted" />
        </button>

        <div className="text-body-2 text-inverted whitespace-nowrap">
          {currentIndex + 1} / {items.length}
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden md:block relative px-12 py-6">
        <div className="flex items-center justify-between">
          <div className="text-body-2 text-inverted truncate">{title ?? ''}</div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className={cn('p-3 rounded-full')}
          >
            <IconCross className="h-4 w-4 icon-inverted" />
          </button>
        </div>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-body-2 text-inverted whitespace-nowrap">
          {currentIndex + 1} / {items.length}
        </div>
      </div>

      {/* Carousel */}
      <div className="flex-1 flex items-center justify-around md:px-12">
        {items.length > 1 && (
          <button
            type="button"
            aria-label="Previous image"
            onClick={() => dispatchControl('prev')}
            className={cn(
              'hidden md:flex bg-primary',
              'z-10',
              'h-8 w-8 p-2 items-center justify-center rounded-full',
              'bg-bg-transparent shadow-md',
            )}
          >
            <IconChevronLeft className="h-3 w-3 icon-primary" />
          </button>
        )}
        <div className="w-full md:max-w-[1280px] md:px-12">
          <Carousel
            carouselId={carouselId}
            items={items.map((item, index) => ({
              id: item.id,
              content: (
                <div className="relative w-full h-[293px] md:h-auto md:aspect-[300/200]">
                  <NextImage
                    src={item.src}
                    alt={item.alt}
                    fill
                    sizes="100vw"
                    className="object-contain"
                    priority={index === initialIndex}
                  />
                </div>
              ),
            }))}
            perView={1}
            peek={0}
            gap={0}
            ariaLabel="Fullscreen carousel"
          />
        </div>
        {items.length > 1 && (
          <button
            type="button"
            aria-label="Next image"
            onClick={() => dispatchControl('next')}
            className={cn(
              'hidden md:flex bg-primary',
              'z-10',
              'h-8 w-8 p-2 items-center justify-center rounded-full',
              'bg-bg-transparent shadow-md',
            )}
          >
            <IconChevronRight className="h-3 w-3 icon-primary" />
          </button>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(fullscreen, document.body) : null;
}
